import type { Dispatch, SetStateAction } from 'react'
import React, { useContext as useContextCore } from 'react'
import { useLocalStorage } from 'react-use'
import type { UseListResult } from '@ztechapps/common.hook'
import { StorageType, useList } from '@ztechapps/common.hook'
import { BlogListViewModeEnum } from './BlogListViewModeEnum'
import { queries } from './graphql'

const BLOG_LIST_VIEW_MODE_STORAGE_KEY = 'blog-list-view-mode-v2'

// `list` stays loosely typed (UseListResult's default `T = any`) — `queries.allConfig`
// (buildGraphqlQuery) does not infer full `Blog` shapes, and Layout.tsx spreads each row
// straight into `BlogBox`'s required props, so narrowing to `Blog` here breaks that spread.
export type BlogListContextType = UseListResult & {
  filterStorageKey: string
  tenantFree: boolean
  viewMode: BlogListViewModeEnum
  setViewMode: Dispatch<SetStateAction<BlogListViewModeEnum | undefined>>
}

const context = React.createContext<BlogListContextType>({} as BlogListContextType)
const CoreProvider = context.Provider

export type BlogListProviderProps = {
  children: React.ReactNode
  /** The blog categories carry no tenant id (Payroll), so the tree checks the tenant-free reserved pair. */
  tenantFree: boolean
}

export const BlogListProvider = ({ children, tenantFree }: BlogListProviderProps) => {
  const filterStorageKey = 'filter_blogs'

  // Tree is the default: it is where a blog's category is chosen and read.
  const [viewMode, setViewMode] = useLocalStorage<BlogListViewModeEnum>(
    BLOG_LIST_VIEW_MODE_STORAGE_KEY,
    BlogListViewModeEnum.Tree
  )

  // The tree counts and tabulates every blog, so it loads them all (`limit: 0`); the List layout pages by infinite scroll.
  const listResult = useList({
    ...queries.allConfig,
    limit: (viewMode ?? BlogListViewModeEnum.Tree) === BlogListViewModeEnum.Tree ? 0 : undefined,
    sort: { updatedAt: -1 },
    subscribeToUpdates: true,
    listActionOptions: {
      storageKey: filterStorageKey,
      storageType: StorageType.sessionStorage,
    },
  })

  return (
    <CoreProvider
      value={{
        ...listResult,
        filterStorageKey,
        tenantFree,
        viewMode: viewMode ?? BlogListViewModeEnum.Tree,
        setViewMode,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useBlogListContext = () => useContextCore(context)
